import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {InformesApi, listOf} from '../../../api/informesApi';
import {Spinner} from '../../Spinner';

/**
 * El chip de PLANTILLA del folio (mockup): nombre + caret que despliega las plantillas de la
 * agencia (aplicar = copiar su hoja acá), el estado «Modificada» que PULSA en cada mutación de la
 * hoja, y el split «Guardar plantilla» (Actualizar / Guardar como nueva…).
 *
 * `pulseKey` cambia en cada mutación de la plantilla: se usa como `key` del estado para remontar
 * la animación (una clase que ya está puesta no se re-anima).
 */
export function TemplateChip({template, templateModified, pulseKey, onApply, onUpdate, onSaveAsNew}) {
    const {t} = useTranslation();
    const [listOpen, setListOpen] = useState(false);
    const [saveOpen, setSaveOpen] = useState(false);
    const [templates, setTemplates] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const listRef = useRef(null);
    const saveRef = useRef(null);

    useEffect(() => {
        if (!listOpen && !saveOpen) return undefined;
        const onClickOutside = (e) => {
            if (listRef.current && !listRef.current.contains(e.target)) setListOpen(false);
            if (saveRef.current && !saveRef.current.contains(e.target)) setSaveOpen(false);
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, [listOpen, saveOpen]);

    const openList = () => {
        const next = !listOpen;
        setListOpen(next);
        if (next && templates === null) {
            setLoading(true);
            InformesApi.listTemplates().then((r) => {
                setLoading(false);
                // Un fallo NO es «no hay plantillas» (C-informes-28).
                if (!r || r.status !== 'OK') { setLoadError(true); setTemplates(null); return; }
                setLoadError(false);
                setTemplates(listOf(r, 'templates'));
            });
        }
    };

    const hasTemplate = !!(template && template.id);

    return (
        <div className="inf-tpl-chip-wrap">
            <div className="inf-tpl-chip-anchor" ref={listRef}>
                <button type="button" className={`inf-tpl-chip ${hasTemplate ? '' : '-unsaved'}`} aria-haspopup="menu" aria-expanded={listOpen}
                        onClick={openList} data-testid="template-chip">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <rect x="4" y="4" width="16" height="16" rx="2"/><path d="M4 9h16M9 9v11"/>
                    </svg>
                    <span className="inf-tpl-chip-name">{hasTemplate ? template.name : t('informes.template.none', 'Sin plantilla')}</span>
                    <span className="inf-caret">▾</span>
                </button>
                {listOpen && (
                    <div className="inf-dd" role="menu" style={{minWidth: 320}} data-testid="template-list">
                        <div className="inf-dd-sec-head">{t('informes.template.apply_head', 'Aplicar una plantilla (reemplaza esta hoja)')}</div>
                        {loading && <div className="box-loader"><Spinner color="positive small"/></div>}
                        {!loading && loadError && (
                            <div className="inf-dd-empty -error">{t('informes.template.load_error', 'No se pudieron cargar las plantillas.')}</div>
                        )}
                        {!loading && !loadError && templates && templates.length === 0 && (
                            <div className="inf-dd-empty">{t('informes.template.empty', 'Todavía no hay plantillas guardadas.')}</div>
                        )}
                        {!loading && templates && templates.map((tpl) => (
                            <button key={tpl.id} type="button" role="menuitem" className={`inf-dd-item ${tpl.id === (template && template.id) ? '-current' : ''}`}
                                    onClick={() => { setListOpen(false); onApply(tpl); }}>
                                <span>{tpl.name}</span>
                                {tpl.is_shared && <span className="inf-dd-meta">{t('informes.template.shared', 'compartida')}</span>}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {templateModified && (
                <span key={pulseKey} className="inf-tpl-state pulse" data-testid="template-modified">
                    {t('informes.template.modified', 'Modificada')}
                </span>
            )}

            <div className="inf-split-wrap" ref={saveRef}>
                <span className="inf-split -template">
                    <button type="button" className="inf-split-main" data-testid="save-template"
                            onClick={() => (hasTemplate ? onUpdate() : onSaveAsNew())}>
                        {t('informes.template.save', 'Guardar plantilla')}
                    </button>
                    <button type="button" className="inf-split-caret" aria-haspopup="menu" aria-expanded={saveOpen}
                            aria-label={t('informes.template.save_options', 'Opciones de guardado de plantilla')} onClick={() => setSaveOpen(!saveOpen)}>▾</button>
                </span>
                {saveOpen && (
                    <div className="inf-dd" role="menu" style={{minWidth: 260}}>
                        <button type="button" role="menuitem" className="inf-dd-item" disabled={!hasTemplate}
                                onClick={() => { setSaveOpen(false); onUpdate(); }}>{t('informes.template.update', 'Actualizar la plantilla')}</button>
                        <button type="button" role="menuitem" className="inf-dd-item"
                                onClick={() => { setSaveOpen(false); onSaveAsNew(); }}>{t('informes.template.save_as_new_item', 'Guardar como plantilla nueva…')}</button>
                    </div>
                )}
            </div>
        </div>
    );
}
